/**
 * Rendering for calculated values (spec sections 1.1, 18, 28).
 *
 * A fact from Jeabs renders plainly. Anything this system worked out for itself
 * — a score, a growth flag, a "likely rally lead" — must arrive with its
 * confidence visible and its components openable. That is the difference
 * between a tool an R4 can argue with and a tool that just asserts things.
 *
 * The Korean copy keeps the same distinction with 추론값 / 추정값 rather than a
 * flat noun: a label that reads as a plain fact would erase the one thing this
 * component exists to show.
 */

import type { ReactNode } from 'react';
import { Badge } from '../ui/primitives';
import { formatRatioAsPercent } from '../../lib/format';
import { useT } from '../../i18n';

type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN';

/** Below this, a mean or median is not something to plan around (spec §28). */
export const LOW_SAMPLE_THRESHOLD = 5;

export function ConfidenceBadge({
  level,
  confidence,
}: {
  level: ConfidenceLevel;
  confidence?: number | null;
}) {
  const t = useT();
  const numeric =
    confidence === null || confidence === undefined ? null : formatRatioAsPercent(confidence);
  return (
    <Badge
      tone={level}
      title={
        numeric ? t.inference.confidenceTitleWith(numeric) : t.inference.confidenceTitle
      }
    >
      {level}
      {numeric ? <span className="faint">{numeric}</span> : null}
    </Badge>
  );
}

/**
 * Labels a block as inference rather than fact. Used as a heading suffix so a
 * reader scanning a screen can tell at a glance which numbers the game
 * reported and which ones we derived.
 */
export function InferenceMark({ modelVersion }: { modelVersion?: string | null }) {
  const t = useT();
  return (
    <span
      className="inference-mark"
      title={
        modelVersion
          ? t.inference.calculatedTitleWith(modelVersion)
          : t.inference.calculatedTitle
      }
    >
      {t.inference.calculated}
      {modelVersion ? ` · ${modelVersion}` : ''}
    </span>
  );
}

/**
 * Sample count with a visible low-sample warning.
 *
 * Every mean, median or "best" in this app must be accompanied by one of these.
 * A 1.9M average over 2 rallies and over 40 rallies are different claims and
 * must not look the same.
 *
 * @param noun Pass one of `t.inference.nouns`, never a literal — the noun is
 *   part of the sentence and has to be translated with it.
 */
export function SampleCount({
  samples,
  noun,
  threshold = LOW_SAMPLE_THRESHOLD,
}: {
  samples: number;
  noun?: string;
  threshold?: number;
}) {
  const t = useT();
  const unit = noun ?? t.inference.nouns.samples;

  if (samples === 0) {
    return (
      <span className="badge badge--UNKNOWN" title={t.inference.noSamplesTitle(unit)}>
        {t.inference.noSamples(unit)}
      </span>
    );
  }
  if (samples < threshold) {
    return (
      <span className="badge badge--LOW" title={t.inference.lowSamplesTitle(samples, unit, threshold)}>
        {t.inference.lowSamples(samples, unit)}
      </span>
    );
  }
  return (
    <span className="badge badge--neutral" title={t.inference.samplesTitle(samples, unit)}>
      {t.inference.samples(samples, unit)}
    </span>
  );
}

/**
 * The openable breakdown behind a score (spec section 18).
 *
 * Components are shown as signed contributions with a bar scaled to the largest
 * absolute component, so it is immediately obvious which input is actually
 * driving the number.
 *
 * Component names arrive from the server as an open-ended record, so they are
 * rendered verbatim rather than mapped through the dictionary: a component this
 * app has never seen must show its real name, not a blank.
 */
export function ScoreBreakdown({
  components,
  warnings = [],
}: {
  components: Record<string, number>;
  warnings?: readonly string[];
}) {
  const t = useT();
  const entries = Object.entries(components);
  if (entries.length === 0) {
    return (
      <p className="small muted" style={{ margin: '6px 0 0' }}>
        {t.inference.noComponents}
      </p>
    );
  }

  const max = Math.max(...entries.map(([, value]) => Math.abs(value)), 1e-9);

  return (
    <div className="breakdown">
      {entries.map(([key, value]) => (
        <div className="breakdown__row" key={key}>
          <div>
            <div className="small muted">{key}</div>
            <div className="breakdown__bar">
              <div
                className={
                  value < 0 ? 'breakdown__bar-fill breakdown__bar-fill--negative' : 'breakdown__bar-fill'
                }
                style={{ width: `${(Math.abs(value) / max) * 100}%` }}
              />
            </div>
          </div>
          <div className="num small">{value.toFixed(2)}</div>
        </div>
      ))}
      {warnings.length > 0 ? (
        <ul className="small muted" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Collapsed wrapper so a breakdown is one click away without dominating a row. */
export function ExplainToggle({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <details>
      <summary>{label ?? t.inference.whyThisScore}</summary>
      {children}
    </details>
  );
}
